import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { BookExercise } from '../../types';
import BookContentApiService from '../../services/bookContentApi';
import VideoPlayer from '../common/VideoPlayer';
import { AppEnvironment } from '../../config/environment';
import { toUserMessage } from '../../lib/http';

/** Formate une durée en secondes vers mm:ss (ou h:mm:ss au-delà d'une heure). */
export const formatSecondsToMMSS = (totalSeconds: number): string => {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
};

interface BookExerciseFormProps {
  open: boolean;
  exercise: BookExercise | null;
  lessonId: string;
  onClose: () => void;
  onSave: () => void;
  env: AppEnvironment;
}

const BookExerciseForm: React.FC<BookExerciseFormProps> = ({
  open,
  exercise,
  lessonId,
  onClose,
  onSave,
  env
}) => {
  const [formData, setFormData] = useState<Partial<BookExercise>>({
    lessonId: lessonId,
    exerciseNumber: 1,
    title: '',
    statement: '',
    correction: '',
    illustrationUrl: '',
    illustrationLatex: '',
    difficulty: 'medium',
    points: 10,
    hints: [],
    keywords: [],
    status: 'draft',
    url: '',
    resume: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [newHint, setNewHint] = useState('');
  const [newKeyword, setNewKeyword] = useState('');

  useEffect(() => {
    if (exercise) {
      setFormData(exercise);
    } else {
      setFormData({
        lessonId: lessonId,
        exerciseNumber: 1,
        title: '',
        statement: '',
        correction: '',
        illustrationUrl: '',
        illustrationLatex: '',
        difficulty: 'medium',
        points: 10,
        hints: [],
        keywords: [],
        status: 'draft',
        url: '',
        resume: ''
      });
    }
  }, [exercise, lessonId]);

  const handleChange = <Field extends keyof BookExercise>(field: Field, value: BookExercise[Field]) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleAddHint = () => {
    if (newHint.trim()) {
      const hints = [...(formData.hints || []), newHint.trim()];
      setFormData({ ...formData, hints });
      setNewHint('');
    }
  };

  const handleRemoveHint = (index: number) => {
    const hints = [...(formData.hints || [])];
    hints.splice(index, 1);
    setFormData({ ...formData, hints });
  };

  const handleAddKeyword = () => {
    if (newKeyword.trim()) {
      const keywords = [...(formData.keywords || []), newKeyword.trim()];
      setFormData({ ...formData, keywords });
      setNewKeyword('');
    }
  };

  const handleRemoveKeyword = (index: number) => {
    const keywords = [...(formData.keywords || [])];
    keywords.splice(index, 1);
    setFormData({ ...formData, keywords });
  };

  const validateForm = (): boolean => {
    if (!formData.title || formData.title.trim().length < 3) {
      setError('Le titre doit contenir au moins 3 caractères');
      return false;
    }
    // Validation énoncé désactivée (caché pour copyright)
    // if (!formData.statement || formData.statement.trim().length < 10) {
    //   setError('L\'énoncé doit contenir au moins 10 caractères');
    //   return false;
    // }
    // Validation correction désactivée (caché pour copyright)
    // if (!formData.correction || formData.correction.trim().length < 10) {
    //   setError('La correction doit contenir au moins 10 caractères');
    //   return false;
    // }
    if (!formData.exerciseNumber || formData.exerciseNumber < 1) {
      setError('Le numéro d\'exercice doit être supérieur ou égal à 1');
      return false;
    }
    if (!formData.points || formData.points < 1 || formData.points > 100) {
      setError('Les points doivent être entre 1 et 100');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setError('');
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (exercise?.id) {
        await BookContentApiService.updateBookExercise(env, exercise.id, formData);
      } else {
        await BookContentApiService.createBookExercise(env, formData as Omit<BookExercise, 'id'>);
      }
      onSave();
      onClose();
    } catch (err: unknown) {
      setError(toUserMessage(err, 'Erreur lors de la sauvegarde'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {exercise ? 'Modifier l\'exercice' : 'Nouvel exercice'}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Numéro de l'exercice"
              type="number"
              value={formData.exerciseNumber}
              onChange={(e) => handleChange('exerciseNumber', parseInt(e.target.value))}
              required
              inputProps={{ min: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth required>
              <InputLabel>Difficulté</InputLabel>
              <Select
                value={formData.difficulty}
                label="Difficulté"
                onChange={(e) => handleChange('difficulty', e.target.value as BookExercise['difficulty'])}
              >
                <MenuItem value="easy">Facile</MenuItem>
                <MenuItem value="medium">Moyen</MenuItem>
                <MenuItem value="hard">Difficile</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Points"
              type="number"
              value={formData.points}
              onChange={(e) => handleChange('points', parseInt(e.target.value))}
              required
              inputProps={{ min: 1, max: 100 }}
              helperText="Entre 1 et 100"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Titre"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              required
            />
          </Grid>

          {/* Énoncé caché pour raisons de copyright */}
          {/* <Grid item xs={12}>
            <TextField
              fullWidth
              label="Énoncé"
              value={formData.statement}
              onChange={(e) => handleChange('statement', e.target.value)}
              required
              multiline
              rows={6}
              helperText="Minimum 10 caractères. Vous pouvez utiliser du LaTeX entre $$ pour les formules mathématiques."
            />
          </Grid> */}

          {/* Correction cachée pour raisons de copyright */}
          {/* <Grid item xs={12}>
            <TextField
              fullWidth
              label="Correction"
              value={formData.correction}
              onChange={(e) => handleChange('correction', e.target.value)}
              required
              multiline
              rows={6}
              helperText="Minimum 10 caractères. Vous pouvez utiliser du LaTeX entre $$ pour les formules mathématiques."
            />
          </Grid> */}

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="URL de l'illustration (optionnel)"
              value={formData.illustrationUrl}
              onChange={(e) => handleChange('illustrationUrl', e.target.value)}
              placeholder="https://..."
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="LaTeX de l'illustration (optionnel)"
              value={formData.illustrationLatex}
              onChange={(e) => handleChange('illustrationLatex', e.target.value)}
              placeholder="\\begin{tikzpicture}..."
            />
          </Grid>

          {/* Video Section */}
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="URL de la vidéo de correction (optionnel)"
              value={formData.url}
              onChange={(e) => handleChange('url', e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              helperText="Lien vers la vidéo explicative de la correction (YouTube)"
            />
          </Grid>

          {/* Durée de la vidéo — auto au rendu, corrigeable à la main */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label="Durée de la vidéo (secondes)"
              value={formData.videoDuration ?? ''}
              onChange={(event) =>
                handleChange(
                  'videoDuration',
                  event.target.value === '' ? undefined : Math.max(0, parseInt(event.target.value, 10) || 0)
                )
              }
              inputProps={{ min: 0, max: 86400 }}
              placeholder="ex. 125"
              helperText={
                formData.videoDuration
                  ? `≈ ${formatSecondsToMMSS(formData.videoDuration)} — renseignée auto au rendu, corrigeable`
                  : 'Renseignée automatiquement à la génération ; saisissez-la si absente'
              }
            />
          </Grid>

          {/* Video Preview */}
          {formData.url && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Prévisualisation de la vidéo
              </Typography>
              <VideoPlayer
                youtubeUrl={formData.url}
                title={formData.title || 'Vidéo de correction'}
                aspectRatio="16/9"
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Résumé de la vidéo (optionnel)"
              value={formData.resume}
              onChange={(e) => handleChange('resume', e.target.value)}
              multiline
              rows={4}
              helperText="Résumé écrit du contenu de la vidéo de correction"
            />
          </Grid>

          {/* Hints Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Indices (optionnels)
            </Typography>
            <Box display="flex" gap={1} mb={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Ajouter un indice"
                value={newHint}
                onChange={(e) => setNewHint(e.target.value)}
                onKeyPress={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleAddHint();
                  }
                }}
              />
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={handleAddHint}
              >
                Ajouter
              </Button>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {(formData.hints || []).map((hint, index) => (
                <Chip
                  key={index}
                  label={hint}
                  onDelete={() => handleRemoveHint(index)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Grid>

          {/* Keywords Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Mots-clés (optionnels)
            </Typography>
            <Box display="flex" gap={1} mb={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Ajouter un mot-clé"
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                onKeyPress={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleAddKeyword();
                  }
                }}
              />
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={handleAddKeyword}
              >
                Ajouter
              </Button>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {(formData.keywords || []).map((keyword, index) => (
                <Chip
                  key={index}
                  label={keyword}
                  onDelete={() => handleRemoveKeyword(index)}
                  color="secondary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Statut</InputLabel>
              <Select
                value={formData.status}
                label="Statut"
                onChange={(e) => handleChange('status', e.target.value as BookExercise['status'])}
              >
                <MenuItem value="draft">Brouillon</MenuItem>
                <MenuItem value="published">Publié</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Annuler</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookExerciseForm;
